import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MarkdownView from "@/components/MarkdownView";
import MarkdownEditor from "@/components/MarkdownEditor";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowUp, ArrowDown, ChevronLeft, Trash2, MessageSquare, X } from "lucide-react";

function VoteBar({ score, myVote, onVote, testId }) {
  return (
    <div className="flex flex-col items-center gap-0.5 shrink-0 w-9">
      <button onClick={() => onVote(myVote === 1 ? 0 : 1)}
        className={`p-1 rounded-sm hover:bg-slate-100 ${myVote === 1 ? "text-kb-primary" : "text-kb-ink-muted"}`}
        data-testid={`${testId}-upvote`}>
        <ArrowUp className="w-4 h-4" />
      </button>
      <div className="kb-mono text-xs text-kb-ink">{score || 0}</div>
      <button onClick={() => onVote(myVote === -1 ? 0 : -1)}
        className={`p-1 rounded-sm hover:bg-slate-100 ${myVote === -1 ? "text-rose-600" : "text-kb-ink-muted"}`}
        data-testid={`${testId}-downvote`}>
        <ArrowDown className="w-4 h-4" />
      </button>
    </div>
  );
}

function PostBody({ body }) {
  // Auto-link @mentions
  if (!body) return null;
  const linked = body.replace(/(^|\s)(@[a-z0-9_-]+)/gi, "$1[$2](#mention)");
  return <MarkdownView source={linked} />;
}

export default function DiscussionThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [reply, setReply] = useState("");
  const [replyTo, setReplyTo] = useState(null); // parent post when replying inline
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get(`/discussions/threads/${id}`),
        api.get(`/discussions/threads/${id}/posts`),
      ]);
      setThread(t.data); setPosts(p.data || []);
    } catch { toast.error("Thread not found"); navigate("/discussions"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const voteThread = async (value) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    try {
      const r = await api.post(`/discussions/threads/${id}/vote`, { value });
      setThread((t) => ({ ...t, score: r.data.score, my_vote: r.data.my_vote }));
    } catch { toast.error("Vote failed"); }
  };
  const votePost = async (postId, value) => {
    if (!user) { toast.error("Sign in to vote"); return; }
    try {
      const r = await api.post(`/discussions/posts/${postId}/vote`, { value });
      setPosts((ps) => ps.map((p) => p.id === postId ? { ...p, score: r.data.score, my_vote: r.data.my_vote } : p));
    } catch { toast.error("Vote failed"); }
  };
  const submitReply = async () => {
    if (!reply.trim()) return;
    try {
      await api.post(`/discussions/threads/${id}/posts`, { body: reply, parent_id: replyTo?.id || null });
      setReply(""); setReplyTo(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeThread = async () => {
    if (!window.confirm("Delete this thread?")) return;
    await api.delete(`/discussions/threads/${id}`); toast.success("Deleted"); navigate("/discussions");
  };
  const removePost = async (pid) => {
    if (!window.confirm("Delete this reply?")) return;
    await api.delete(`/discussions/posts/${pid}`); load();
  };

  // Group posts: top-level + children
  const { topLevel, childrenOf } = useMemo(() => {
    const top = [], byParent = new Map();
    for (const p of posts) {
      if (!p.parent_id) top.push(p);
      else {
        if (!byParent.has(p.parent_id)) byParent.set(p.parent_id, []);
        byParent.get(p.parent_id).push(p);
      }
    }
    return { topLevel: top, childrenOf: byParent };
  }, [posts]);

  const canModerate = useMemo(() => user && (user.role === "admin" || (thread && thread.author_user_id === user.user_id)), [user, thread]);

  // Build flat rendering rows for replies: top-level + their children (one level)
  const flatRows = useMemo(() => {
    const rows = [];
    for (const top of topLevel) {
      rows.push({ post: top, depth: 0 });
      const kids = childrenOf.get(top.id) || [];
      for (const k of kids) {
        rows.push({ post: k, depth: 1 });
      }
    }
    return rows;
  }, [topLevel, childrenOf]);

  if (loading || !thread) return <div className="min-h-screen flex items-center justify-center kb-mono text-xs text-kb-ink-muted">Loading…</div>;

  const renderReplyRow = (post, depth) => {
    const me = user && (user.role === "admin" || post.author_user_id === user.user_id);
    return (
      <div key={post.id} style={{ marginLeft: depth * 32 }}>
        <div className="kb-card p-4" data-testid={`thread-post-${post.id}`}>
          <div className="flex gap-3">
            <VoteBar score={post.score} myVote={post.my_vote} onVote={(v) => votePost(post.id, v)} testId={`thread-post-${post.id}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{post.author_name}</span>
                {post.author_verified && <span className="kb-mono text-[9px] uppercase tracking-wider text-emerald-700">✓</span>}
                <span className="kb-mono text-[10px] text-kb-ink-light">· {new Date(post.created_at).toLocaleString()}</span>
                {me && (
                  <button onClick={() => removePost(post.id)} className="ml-auto text-rose-600 text-[10px] inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
              </div>
              <div className="mt-2"><PostBody body={post.body} /></div>
              {user && depth === 0 && (
                <div className="mt-2">
                  <button
                    onClick={() => { setReplyTo(post); setReply(`@${post.author_name?.split(" ")[0]?.toLowerCase() || "user"} `); document.querySelector('[data-testid="thread-reply-input"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                    className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-1"
                    data-testid={`thread-post-${post.id}-reply`}
                  >
                    <MessageSquare className="w-3 h-3" /> Reply
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="discussion-thread-page">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <Link to="/discussions" className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-1 hover:text-kb-primary" data-testid="thread-back-link">
          <ChevronLeft className="w-3.5 h-3.5" /> All discussions
        </Link>

        <article className="mt-4 kb-card p-6 flex gap-4">
          <VoteBar score={thread.score} myVote={thread.my_vote} onVote={voteThread} testId="thread-detail" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary">{thread.category}</span>
                <span className="kb-mono text-[10px] text-kb-ink-muted">{thread.author_name}</span>
                {thread.author_verified && <span className="kb-mono text-[9px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">VERIFIED</span>}
                <span className="kb-mono text-[10px] text-kb-ink-light">· {new Date(thread.created_at).toLocaleString()}</span>
              </div>
              {canModerate && (
                <button onClick={removeThread} className="text-rose-600 text-xs inline-flex items-center gap-1" data-testid="thread-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
            <h1 className="font-serif text-2xl lg:text-3xl text-kb-ink mt-2 leading-tight">{thread.title}</h1>
            {thread.body && <div className="mt-3"><PostBody body={thread.body} /></div>}
          </div>
        </article>

        <div className="mt-8">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">{posts.length} REPLIES</div>
          {user ? (
            <div className="mt-3 kb-card p-4">
              {replyTo && (
                <div className="mb-2 kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-2">
                  Replying to {replyTo.author_name}
                  <button onClick={() => { setReplyTo(null); setReply(""); }} className="text-kb-ink-muted"><X className="w-3 h-3" /></button>
                </div>
              )}
              <MarkdownEditor value={reply} onChange={setReply} data-testid="thread-reply-input" />
              <div className="mt-2 flex justify-end gap-2">
                {replyTo && <button onClick={() => { setReplyTo(null); setReply(""); }} className="kb-btn-outline text-sm">Cancel</button>}
                <button onClick={submitReply} className="kb-btn-primary text-sm" data-testid="thread-reply-submit">
                  {replyTo ? "Post reply" : "Post comment"}
                </button>
              </div>
              <div className="mt-2 kb-mono text-[10px] text-kb-ink-light">
                Tip: Markdown supported. Mention researchers with @name. Verified-only thread starts.
              </div>
            </div>
          ) : (
            <div className="mt-3 border border-slate-200 bg-white p-4 rounded-sm text-sm text-kb-ink-muted">
              <Link to="/login" className="text-kb-primary">Sign in</Link> to reply or vote.
            </div>
          )}

          <div className="mt-4 space-y-3">
            {flatRows.map(({ post, depth }) => renderReplyRow(post, depth))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
